/**
 * =============================================================================
 * ALERT CATEGORY VIEW COMPONENT
 * =============================================================================
 * 
 * Shows categorized alerts with tabs: Pending | Done | Snoozed
 * Each tab shows count of alerts in that category.
 * 
 * FEATURES:
 * - Sort: By due date or created date
 * - Filter: By alert type
 * 
 * API INTEGRATION:
 * - fetchAlertsByCategory() - Fetches all alerts grouped by status
 * 
 * INTERACTIONS:
 * - Tab click - switches between categories (local state)
 * - Alert click - calls onAlertClick prop to open chat
 */

import { Box, Stack, Text, Group, Badge, Tabs, Loader, Menu, ActionIcon } from '@mantine/core';
import {
    ClockCounterClockwise,
    CheckCircle,
    Bell,
    Warning,
    SortAscending,
    Funnel,
} from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import AlertListItem from './AlertListItem';
import { fetchAlertsByCategory } from './api';
import type { AlertItem, CategorizedAlerts, AlertCategory } from './types';

interface AlertCategoryViewProps {
    /** Called when user clicks an alert to open chat */
    onAlertClick: (alert: AlertItem) => void;
}

// Sort options
type SortOption = 'dueDate' | 'createdAt';
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'dueDate', label: 'Due Date' },
    { value: 'createdAt', label: 'Created Date' },
];

// =============================================================================
// COMPONENT
// =============================================================================

const AlertCategoryView: React.FC<AlertCategoryViewProps> = memo(({ onAlertClick }) => {
    // =========================================================================
    // STATE
    // =========================================================================
    const [activeTab, setActiveTab] = useState<AlertCategory>('pending');
    const [alerts, setAlerts] = useState<CategorizedAlerts>({
        pending: [],
        done: [],
        snoozed: [],
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    
    // Sort & Filter state
    const [sortBy, setSortBy] = useState<SortOption>('dueDate');
    const [filterAlertType, setFilterAlertType] = useState<string | null>(null);

    // =========================================================================
    // API CALL
    // Fetches categorized alerts on mount
    // =========================================================================
    useEffect(() => {
        const loadAlerts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const categorized = await fetchAlertsByCategory();
                setAlerts(categorized);
            } catch (err) {
                setError('Failed to load alerts. Please try again.');
                console.error('Error fetching alerts:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadAlerts();
    }, []);

    // =========================================================================
    // COMPUTED - Get unique alert types for filter dropdown
    // =========================================================================
    const alertTypes = useMemo(() => {
        const allAlerts = [...alerts.pending, ...alerts.done, ...alerts.snoozed];
        const types = new Set(allAlerts.map((a) => a.alertTypeLabel));
        return Array.from(types).map((t) => ({ value: t, label: t }));
    }, [alerts]);

    // =========================================================================
    // COMPUTED - Filtered and sorted alerts
    // =========================================================================
    const currentAlerts = useMemo(() => {
        let result = [...alerts[activeTab]];
        
        // Apply filter
        if (filterAlertType) {
            result = result.filter((a) => a.alertTypeLabel === filterAlertType);
        }
        
        // Apply sort
        result.sort((a, b) => {
            if (sortBy === 'dueDate') {
                const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
                const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
                return dateA - dateB; // Ascending - earliest first
            } else {
                const dateA = new Date(a.createdAt).getTime();
                const dateB = new Date(b.createdAt).getTime();
                return dateB - dateA; // Descending - newest first
            }
        });
        
        return result;
    }, [alerts, activeTab, sortBy, filterAlertType]);

    // =========================================================================
    // HANDLERS
    // =========================================================================
    
    const handleTabChange = useCallback((value: string | null) => {
        if (value) {
            setActiveTab(value as AlertCategory);
            setHoveredIndex(null);
        }
    }, []);

    const handleAlertClick = useCallback((alert: AlertItem) => {
        onAlertClick(alert);
    }, [onAlertClick]);

    // =========================================================================
    // RENDER
    // =========================================================================
    
    if (isLoading) {
        return (
            <Box className="flex items-center justify-center py-12">
                <Loader size="md" color="blue" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="flex flex-col items-center justify-center py-12">
                <Warning size={40} weight="light" className="text-halt-800 mb-3" />
                <Text className="text-sm text-halt-800">{error}</Text>
            </Box>
        );
    }

    return (
        <Stack className="gap-4 h-full">
            {/* =============================================================
                SORT & FILTER BAR
                ============================================================= */}
            <Group className="justify-between items-center">
                <Text className="text-sm font-medium text-gray-600">
                    {currentAlerts.length} {activeTab} alert{currentAlerts.length !== 1 ? 's' : ''}
                </Text>
                
                <Group className="gap-2">
                    {/* Sort Dropdown */}
                    <Menu shadow="md" width={180} position="bottom-end">
                        <Menu.Target>
                            <ActionIcon variant="light" color="gray" size="md">
                                <SortAscending size={18} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>Sort by</Menu.Label>
                            {SORT_OPTIONS.map((opt) => (
                                <Menu.Item
                                    key={opt.value}
                                    onClick={() => setSortBy(opt.value)}
                                    className={sortBy === opt.value ? 'bg-blu-50' : ''}
                                >
                                    {opt.label}
                                </Menu.Item>
                            ))}
                        </Menu.Dropdown>
                    </Menu>
                    
                    {/* Filter Dropdown */}
                    <Menu shadow="md" width={200} position="bottom-end">
                        <Menu.Target>
                            <ActionIcon 
                                variant="light" 
                                color={filterAlertType ? 'blue' : 'gray'} 
                                size="md"
                            >
                                <Funnel size={18} weight={filterAlertType ? 'fill' : 'regular'} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>Filter by Alert Type</Menu.Label>
                            <Menu.Item
                                onClick={() => setFilterAlertType(null)}
                                className={!filterAlertType ? 'bg-blu-50' : ''}
                            >
                                All Types
                            </Menu.Item>
                            <Menu.Divider />
                            {alertTypes.map((type) => (
                                <Menu.Item
                                    key={type.value}
                                    onClick={() => setFilterAlertType(type.value)}
                                    className={filterAlertType === type.value ? 'bg-blu-50' : ''}
                                >
                                    {type.label}
                                </Menu.Item>
                            ))}
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>
            
            {/* =============================================================
                CATEGORY TABS
                ============================================================= */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="pills"
                classNames={{
                    root: 'w-full',
                    list: 'bg-gray-100 p-1 rounded-xl gap-1',
                    tab: 'rounded-lg font-medium text-sm px-4 py-2 text-gray-600 data-[active=true]:bg-white data-[active=true]:shadow-sm data-[active=true]:text-gray-900',
                    tabLabel: 'text-inherit',
                }}
            >
                <Tabs.List grow>
                    {/* Pending Tab */}
                    <Tabs.Tab
                        value="pending"
                        leftSection={<Bell size={16} weight="fill" />}
                        rightSection={
                            <Badge size="sm" variant="light" color="blue" className="ml-1">
                                {alerts.pending.length}
                            </Badge>
                        }
                    >
                        Pending
                    </Tabs.Tab>

                    {/* Done Tab */}
                    <Tabs.Tab
                        value="done"
                        leftSection={<CheckCircle size={16} weight="fill" />}
                        rightSection={
                            <Badge size="sm" variant="light" color="green" className="ml-1">
                                {alerts.done.length}
                            </Badge>
                        }
                    >
                        Done
                    </Tabs.Tab>

                    {/* Snoozed Tab */}
                    <Tabs.Tab
                        value="snoozed"
                        leftSection={<ClockCounterClockwise size={16} weight="fill" />}
                        rightSection={
                            <Badge size="sm" variant="light" color="grape" className="ml-1">
                                {alerts.snoozed.length}
                            </Badge>
                        }
                    >
                        Snoozed
                    </Tabs.Tab>
                </Tabs.List>
            </Tabs>

            {/* =============================================================
                ALERT LIST
                ============================================================= */}
            <Box className="flex-1 overflow-y-auto scrollbar-hide -mx-1 px-1">
                {currentAlerts.length === 0 ? (
                    <Box className="flex flex-col items-center justify-center py-12">
                        {activeTab === 'pending' ? (
                            <>
                                <CheckCircle size={48} weight="light" className="text-green/50 mb-3" />
                                <Text className="text-base font-semibold text-gray-700">All caught up!</Text>
                                <Text className="text-sm text-gray-500">No pending alerts</Text>
                            </>
                        ) : activeTab === 'done' ? (
                            <>
                                <CheckCircle size={48} weight="light" className="text-gray-300 mb-3" />
                                <Text className="text-sm text-gray-500">No completed alerts yet</Text>
                            </>
                        ) : (
                            <>
                                <ClockCounterClockwise size={48} weight="light" className="text-gray-300 mb-3" />
                                <Text className="text-sm text-gray-500">No snoozed alerts</Text>
                            </>
                        )}
                    </Box>
                ) : (
                    <AnimatePresence mode="popLayout">
                        <Stack className="gap-2">
                            {currentAlerts.map((alert, index) => (
                                /* =====================================================
                                   ALERT CLICK - Line 180
                                   Calls onAlertClick to open chat view
                                   No API call here - parent handles the rest
                                   ===================================================== */
                                <AlertListItem
                                    key={alert.id}
                                    alert={alert}
                                    index={index}
                                    onClick={() => handleAlertClick(alert)}
                                    isHovered={hoveredIndex === index}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                />
                            ))}
                        </Stack>
                    </AnimatePresence>
                )}
            </Box>
        </Stack>
    );
});

AlertCategoryView.displayName = 'AlertCategoryView';

export default AlertCategoryView;
